import { useState } from 'react';
import { useBuilderStore } from '../../stores/builderStore';
import { fieldRegistry } from '../../lib/fieldRegistry';
import type { FormField, FieldOption, ConditionalRule, Condition, ConditionOperator, ConditionalAction, ValidationMessages, MatrixRow, MatrixColumn, RankingItem } from '../../types/form';

export default function FieldConfigurator() {
  const { formSchema, selectedFieldId, updateField, selectField } = useBuilderStore();

  const field = formSchema.fields.find((f) => f.id === selectedFieldId);

  if (!field) {
    return (
      <div className="w-80 bg-white border-l border-gray-200 p-6">
        <div className="text-center text-gray-500">
          <p>Select a field to configure</p>
        </div>
      </div>
    );
  }

  const currentField = field; // For use in closures
  const fieldDef = fieldRegistry[currentField.type];

  function handleChange<K extends keyof FormField>(key: K, value: FormField[K]) {
    updateField(currentField.id, { [key]: value });
  }

  function handleOptionsChange(options: FieldOption[]) {
    updateField(currentField.id, { options });
  }

  function addOption() {
    const newOption: FieldOption = {
      label: `Option ${(currentField.options?.length || 0) + 1}`,
      value: `option${(currentField.options?.length || 0) + 1}`,
    };
    handleOptionsChange([...(currentField.options || []), newOption]);
  }

  function updateOption(index: number, updates: Partial<FieldOption>) {
    const newOptions = [...(currentField.options || [])];
    newOptions[index] = { ...newOptions[index], ...updates };
    handleOptionsChange(newOptions);
  }

  function removeOption(index: number) {
    const newOptions = (currentField.options || []).filter((_, i) => i !== index);
    handleOptionsChange(newOptions);
  }

  return (
    <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 flex items-center justify-center bg-primary-100 text-primary-600 rounded">
              {fieldDef.icon}
            </span>
            <span className="font-medium">{fieldDef.label}</span>
          </div>
          <button
            onClick={() => selectField(null)}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* General Settings */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">General</h3>

        <div className="space-y-4">
          <div>
            <label className="label">Label</label>
            <input
              type="text"
              value={field.label}
              onChange={(e) => handleChange('label', e.target.value)}
              className="input"
            />
          </div>

          <div>
            <label className="label">Field Name</label>
            <input
              type="text"
              value={field.name}
              onChange={(e) => handleChange('name', e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'))}
              className="input font-mono text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">Used in submissions data</p>
          </div>

          <div>
            <label className="label">Placeholder</label>
            <input
              type="text"
              value={field.placeholder || ''}
              onChange={(e) => handleChange('placeholder', e.target.value)}
              className="input"
            />
          </div>

          <div>
            <label className="label">Help Text</label>
            <input
              type="text"
              value={field.helpText || ''}
              onChange={(e) => handleChange('helpText', e.target.value)}
              className="input"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="required"
              checked={field.required}
              onChange={(e) => handleChange('required', e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="required" className="text-sm text-gray-700">
              Required field
            </label>
          </div>
        </div>
      </div>

      {/* Options (for select, radio, checkbox) */}
      {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Options</h3>

          <div className="space-y-2">
            {field.options?.map((option, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={option.label}
                  onChange={(e) => updateOption(index, { label: e.target.value, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                  className="input flex-1"
                  placeholder="Option label"
                />
                <button
                  onClick={() => removeOption(index)}
                  className="p-2 text-gray-400 hover:text-red-600"
                  disabled={field.options!.length <= 1}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={addOption}
            className="mt-3 text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Option
          </button>
        </div>
      )}

      {/* Type-specific Settings */}
      {field.type === 'text' && (
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Validation</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Min Length</label>
                <input
                  type="number"
                  min="0"
                  value={field.properties.minLength || ''}
                  onChange={(e) => updateField(field.id, {
                    properties: { ...field.properties, minLength: e.target.value ? parseInt(e.target.value) : undefined }
                  })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Max Length</label>
                <input
                  type="number"
                  min="0"
                  value={field.properties.maxLength || ''}
                  onChange={(e) => updateField(field.id, {
                    properties: { ...field.properties, maxLength: e.target.value ? parseInt(e.target.value) : undefined }
                  })}
                  className="input"
                />
              </div>
            </div>
            <div>
              <label className="label">Pattern (Regex)</label>
              <input
                type="text"
                value={field.properties.pattern || ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, pattern: e.target.value || undefined }
                })}
                className="input font-mono text-sm"
                placeholder="e.g., ^[A-Z]+$"
              />
            </div>
          </div>
        </div>
      )}

      {/* Custom Validation Messages */}
      <ValidationMessagesPanel field={field} updateField={updateField} />

      {field.type === 'number' && (
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Validation</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Minimum</label>
              <input
                type="number"
                value={field.properties.min ?? ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, min: e.target.value ? parseFloat(e.target.value) : undefined }
                })}
                className="input"
              />
            </div>
            <div>
              <label className="label">Maximum</label>
              <input
                type="number"
                value={field.properties.max ?? ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, max: e.target.value ? parseFloat(e.target.value) : undefined }
                })}
                className="input"
              />
            </div>
            <div className="col-span-2">
              <label className="label">Step</label>
              <input
                type="number"
                min="0"
                step="any"
                value={field.properties.step ?? ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, step: e.target.value ? parseFloat(e.target.value) : undefined }
                })}
                className="input"
              />
            </div>
          </div>
        </div>
      )}

      {field.type === 'file' && (
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">File Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="label">Accepted File Types</label>
              <input
                type="text"
                value={field.properties.accept || ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, accept: e.target.value || undefined }
                })}
                className="input"
                placeholder="e.g., image/*,.pdf"
              />
            </div>
            <div>
              <label className="label">Max File Size (MB)</label>
              <input
                type="number"
                min="1"
                value={(field.properties.maxSize || 10 * 1024 * 1024) / (1024 * 1024)}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, maxSize: parseFloat(e.target.value) * 1024 * 1024 }
                })}
                className="input"
              />
            </div>
            <div>
              <label className="label">Max Files</label>
              <input
                type="number"
                min="1"
                value={field.properties.maxFiles || 1}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, maxFiles: parseInt(e.target.value) }
                })}
                className="input"
              />
            </div>
          </div>
        </div>
      )}

      {field.type === 'select' && (
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="multiple"
              checked={field.properties.multiple || false}
              onChange={(e) => updateField(field.id, {
                properties: { ...field.properties, multiple: e.target.checked }
              })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="multiple" className="text-sm text-gray-700">
              Allow multiple selections
            </label>
          </div>
        </div>
      )}

      {/* Section Settings */}
      {field.type === 'section' && (
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Section Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="label">Section Description</label>
              <textarea
                value={field.properties.sectionDescription || ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, sectionDescription: e.target.value }
                })}
                className="input"
                rows={2}
                placeholder="Optional description for this section"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="collapsible"
                checked={field.properties.collapsible || false}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, collapsible: e.target.checked }
                })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="collapsible" className="text-sm text-gray-700">
                Collapsible section
              </label>
            </div>
            {field.properties.collapsible && (
              <div className="flex items-center gap-2 ml-6">
                <input
                  type="checkbox"
                  id="defaultCollapsed"
                  checked={field.properties.defaultCollapsed || false}
                  onChange={(e) => updateField(field.id, {
                    properties: { ...field.properties, defaultCollapsed: e.target.checked }
                  })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="defaultCollapsed" className="text-sm text-gray-700">
                  Collapsed by default
                </label>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Divider Settings */}
      {field.type === 'divider' && (
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Divider Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="label">Style</label>
              <select
                value={field.properties.dividerStyle || 'solid'}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, dividerStyle: e.target.value as 'solid' | 'dashed' | 'dotted' }
                })}
                className="input"
              >
                <option value="solid">Solid</option>
                <option value="dashed">Dashed</option>
                <option value="dotted">Dotted</option>
              </select>
            </div>
            <div>
              <label className="label">Color</label>
              <input
                type="color"
                value={field.properties.dividerColor || '#e5e7eb'}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, dividerColor: e.target.value }
                })}
                className="input h-10"
              />
            </div>
          </div>
        </div>
      )}

      {/* Page Break Settings */}
      {field.type === 'page_break' && (
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Page Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="label">Page Title</label>
              <input
                type="text"
                value={field.label || ''}
                onChange={(e) => handleChange('label', e.target.value)}
                className="input"
                placeholder="Title for the next page"
              />
            </div>
            <div>
              <label className="label">Page Description</label>
              <textarea
                value={field.properties.pageDescription || ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, pageDescription: e.target.value }
                })}
                className="input"
                rows={2}
                placeholder="Optional description for this page"
              />
            </div>
          </div>
        </div>
      )}

      {/* Rating Settings */}
      {field.type === 'rating' && (
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Rating Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="label">Maximum Rating</label>
              <input
                type="number"
                min="3"
                max="10"
                value={field.properties.ratingMax || 5}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, ratingMax: parseInt(e.target.value) }
                })}
                className="input"
              />
            </div>
            <div>
              <label className="label">Icon Style</label>
              <select
                value={field.properties.ratingIcon || 'star'}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, ratingIcon: e.target.value as 'star' | 'heart' | 'circle' }
                })}
                className="input"
              >
                <option value="star">Stars</option>
                <option value="heart">Hearts</option>
                <option value="circle">Circles</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Slider Settings */}
      {field.type === 'slider' && (
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Slider Settings</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Minimum</label>
                <input
                  type="number"
                  value={field.properties.sliderMin ?? 0}
                  onChange={(e) => updateField(field.id, {
                    properties: { ...field.properties, sliderMin: parseFloat(e.target.value) }
                  })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Maximum</label>
                <input
                  type="number"
                  value={field.properties.sliderMax ?? 100}
                  onChange={(e) => updateField(field.id, {
                    properties: { ...field.properties, sliderMax: parseFloat(e.target.value) }
                  })}
                  className="input"
                />
              </div>
            </div>
            <div>
              <label className="label">Step</label>
              <input
                type="number"
                min="0.01"
                step="any"
                value={field.properties.sliderStep ?? 1}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, sliderStep: parseFloat(e.target.value) }
                })}
                className="input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showValue"
                checked={field.properties.showValue !== false}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, showValue: e.target.checked }
                })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="showValue" className="text-sm text-gray-700">Show current value</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showMinMax"
                checked={field.properties.showMinMax !== false}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, showMinMax: e.target.checked }
                })}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="showMinMax" className="text-sm text-gray-700">Show min/max labels</label>
            </div>
          </div>
        </div>
      )}

      {/* Color Picker Settings */}
      {field.type === 'color' && (
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Color Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="label">Color Format</label>
              <select
                value={field.properties.colorFormat || 'hex'}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, colorFormat: e.target.value as 'hex' | 'rgb' | 'hsl' }
                })}
                className="input"
              >
                <option value="hex">HEX (#RRGGBB)</option>
                <option value="rgb">RGB</option>
                <option value="hsl">HSL</option>
              </select>
            </div>
            <div>
              <label className="label">Preset Colors</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {(field.properties.colorPresets || []).map((color, index) => (
                  <div key={index} className="flex items-center gap-1">
                    <div
                      className="w-6 h-6 rounded border border-gray-300"
                      style={{ backgroundColor: color }}
                    />
                    <button
                      onClick={() => {
                        const newPresets = [...(field.properties.colorPresets || [])];
                        newPresets.splice(index, 1);
                        updateField(field.id, {
                          properties: { ...field.properties, colorPresets: newPresets }
                        });
                      }}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="color"
                  id="newPresetColor"
                  className="w-8 h-8 rounded border border-gray-300"
                />
                <button
                  onClick={() => {
                    const colorInput = document.getElementById('newPresetColor') as HTMLInputElement;
                    const newColor = colorInput.value;
                    updateField(field.id, {
                      properties: {
                        ...field.properties,
                        colorPresets: [...(field.properties.colorPresets || []), newColor]
                      }
                    });
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  + Add preset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden Field Settings */}
      {field.type === 'hidden' && (
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Hidden Field Settings</h3>
          <div className="space-y-4">
            <div>
              <label className="label">Hidden Value</label>
              <input
                type="text"
                value={field.properties.hiddenValue || ''}
                onChange={(e) => updateField(field.id, {
                  properties: { ...field.properties, hiddenValue: e.target.value }
                })}
                className="input"
                placeholder="Value to submit"
              />
              <p className="text-xs text-gray-400 mt-1">
                This value will be submitted with the form but not shown to users.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Matrix Settings */}
      {field.type === 'matrix' && (
        <MatrixSettingsPanel field={field} updateField={updateField} />
      )}

      {/* Ranking Settings */}
      {field.type === 'ranking' && (
        <RankingSettingsPanel field={field} updateField={updateField} />
      )}

      {/* Conditional Logic Section */}
      {field.type !== 'divider' && (
        <ConditionalLogicPanel
          field={field}
          allFields={formSchema.fields}
          conditionalLogic={formSchema.conditionalLogic}
          onUpdate={(rules) => {
            // Update the store's conditional logic
            const store = useBuilderStore.getState();
            store.updateConditionalLogic(rules);
          }}
        />
      )}
    </div>
  );
}

// Conditional Logic Panel Component
interface ConditionalLogicPanelProps {
  field: FormField;
  allFields: FormField[];
  conditionalLogic: ConditionalRule[];
  onUpdate: (rules: ConditionalRule[]) => void;
}

function ConditionalLogicPanel({ field, allFields, conditionalLogic, onUpdate }: ConditionalLogicPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get rules that target this field
  const fieldRules = conditionalLogic.filter(rule => rule.targetFieldId === field.id);

  // Get fields that can be used as conditions (excluding layout fields)
  const conditionFields = allFields.filter(f =>
    f.id !== field.id &&
    !['section', 'divider', 'page_break'].includes(f.type)
  );

  // Get page breaks for skip_to_page action
  const pageBreaks = allFields.filter(f => f.type === 'page_break');

  const operatorLabels: Record<ConditionOperator, string> = {
    equals: 'equals',
    not_equals: 'does not equal',
    contains: 'contains',
    not_contains: 'does not contain',
    greater_than: 'is greater than',
    less_than: 'is less than',
    is_empty: 'is empty',
    is_not_empty: 'is not empty',
  };

  const actionLabels: Record<ConditionalAction, string> = {
    show: 'Show this field',
    hide: 'Hide this field',
    enable: 'Enable this field',
    disable: 'Disable this field',
    require: 'Make required',
    skip_to_page: 'Skip to page',
  };

  function addRule() {
    if (conditionFields.length === 0) return;

    const newRule: ConditionalRule = {
      id: crypto.randomUUID(),
      targetFieldId: field.id,
      targetType: field.type === 'section' ? 'section' : field.type === 'page_break' ? 'page' : 'field',
      action: 'show',
      conditions: [{
        fieldId: conditionFields[0].id,
        operator: 'equals',
        value: '',
      }],
      operator: 'and',
    };

    onUpdate([...conditionalLogic, newRule]);
  }

  function updateRule(ruleId: string, updates: Partial<ConditionalRule>) {
    onUpdate(conditionalLogic.map(rule =>
      rule.id === ruleId ? { ...rule, ...updates } : rule
    ));
  }

  function removeRule(ruleId: string) {
    onUpdate(conditionalLogic.filter(rule => rule.id !== ruleId));
  }

  function addCondition(ruleId: string) {
    if (conditionFields.length === 0) return;

    onUpdate(conditionalLogic.map(rule => {
      if (rule.id !== ruleId) return rule;
      return {
        ...rule,
        conditions: [...rule.conditions, {
          fieldId: conditionFields[0].id,
          operator: 'equals' as ConditionOperator,
          value: '',
        }],
      };
    }));
  }

  function updateCondition(ruleId: string, conditionIndex: number, updates: Partial<Condition>) {
    onUpdate(conditionalLogic.map(rule => {
      if (rule.id !== ruleId) return rule;
      const newConditions = [...rule.conditions];
      newConditions[conditionIndex] = { ...newConditions[conditionIndex], ...updates };
      return { ...rule, conditions: newConditions };
    }));
  }

  function removeCondition(ruleId: string, conditionIndex: number) {
    onUpdate(conditionalLogic.map(rule => {
      if (rule.id !== ruleId) return rule;
      return {
        ...rule,
        conditions: rule.conditions.filter((_, i) => i !== conditionIndex),
      };
    }));
  }

  return (
    <div className="p-4 border-b border-gray-200">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full"
      >
        <h3 className="text-sm font-semibold text-gray-500 uppercase">Conditional Logic</h3>
        <div className="flex items-center gap-2">
          {fieldRules.length > 0 && (
            <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
              {fieldRules.length} rule{fieldRules.length !== 1 ? 's' : ''}
            </span>
          )}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-4">
          {conditionFields.length === 0 ? (
            <p className="text-sm text-gray-500">Add more fields to create conditional logic.</p>
          ) : (
            <>
              {fieldRules.map((rule) => (
                <div key={rule.id} className="bg-gray-50 rounded-lg p-3 space-y-3">
                  {/* Action */}
                  <div className="flex items-center gap-2">
                    <select
                      value={rule.action}
                      onChange={(e) => updateRule(rule.id, { action: e.target.value as ConditionalAction })}
                      className="input text-sm flex-1"
                    >
                      {Object.entries(actionLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => removeRule(rule.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Skip to page selector */}
                  {rule.action === 'skip_to_page' && pageBreaks.length > 0 && (
                    <select
                      value={rule.skipToPageId || ''}
                      onChange={(e) => updateRule(rule.id, { skipToPageId: e.target.value })}
                      className="input text-sm"
                    >
                      <option value="">Select page...</option>
                      {pageBreaks.map((pb) => (
                        <option key={pb.id} value={pb.id}>{pb.label || 'Untitled Page'}</option>
                      ))}
                    </select>
                  )}

                  {/* Conditions */}
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">When:</p>
                    {rule.conditions.map((condition, index) => (
                      <div key={index} className="flex items-center gap-2">
                        {index > 0 && (
                          <select
                            value={rule.operator}
                            onChange={(e) => updateRule(rule.id, { operator: e.target.value as 'and' | 'or' })}
                            className="input text-xs w-16"
                          >
                            <option value="and">AND</option>
                            <option value="or">OR</option>
                          </select>
                        )}
                        <select
                          value={condition.fieldId}
                          onChange={(e) => updateCondition(rule.id, index, { fieldId: e.target.value })}
                          className="input text-xs flex-1"
                        >
                          {conditionFields.map((f) => (
                            <option key={f.id} value={f.id}>{f.label}</option>
                          ))}
                        </select>
                        <select
                          value={condition.operator}
                          onChange={(e) => updateCondition(rule.id, index, { operator: e.target.value as ConditionOperator })}
                          className="input text-xs w-32"
                        >
                          {Object.entries(operatorLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                        {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                          <input
                            type="text"
                            value={String(condition.value || '')}
                            onChange={(e) => updateCondition(rule.id, index, { value: e.target.value })}
                            className="input text-xs w-24"
                            placeholder="Value"
                          />
                        )}
                        {rule.conditions.length > 1 && (
                          <button
                            onClick={() => removeCondition(rule.id, index)}
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => addCondition(rule.id)}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    + Add condition
                  </button>
                </div>
              ))}

              <button
                onClick={addRule}
                className="w-full py-2 text-sm text-primary-600 hover:text-primary-700 border border-dashed border-primary-300 rounded-lg hover:border-primary-400"
              >
                + Add conditional rule
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Validation Messages Panel Component
interface ValidationMessagesPanelProps {
  field: FormField;
  updateField: (fieldId: string, updates: Partial<FormField>) => void;
}

function ValidationMessagesPanel({ field, updateField }: ValidationMessagesPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Skip layout fields
  if (['section', 'divider', 'page_break'].includes(field.type)) {
    return null;
  }

  const messages = field.properties.validationMessages || {};

  function updateMessage(key: keyof ValidationMessages, value: string) {
    updateField(field.id, {
      properties: {
        ...field.properties,
        validationMessages: {
          ...messages,
          [key]: value || undefined,
        },
      },
    });
  }

  // Determine which validation message fields to show based on field type and settings
  const showRequired = true; // All fields can be required
  const showMinLength = field.type === 'text' || field.type === 'textarea';
  const showMaxLength = field.type === 'text' || field.type === 'textarea';
  const showMin = field.type === 'number';
  const showMax = field.type === 'number';
  const showPattern = field.type === 'text' && field.properties.pattern;
  const showEmail = field.type === 'email';

  return (
    <div className="p-4 border-b border-gray-200">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full"
      >
        <h3 className="text-sm font-semibold text-gray-500 uppercase">Error Messages</h3>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-gray-500 mb-3">
            Customize error messages shown when validation fails. Leave blank to use defaults.
          </p>

          {showRequired && (
            <div>
              <label className="label">Required Field Message</label>
              <input
                type="text"
                value={messages.required || ''}
                onChange={(e) => updateMessage('required', e.target.value)}
                className="input text-sm"
                placeholder={`${field.label} is required`}
              />
            </div>
          )}

          {showMinLength && field.properties.minLength && (
            <div>
              <label className="label">Min Length Message</label>
              <input
                type="text"
                value={messages.minLength || ''}
                onChange={(e) => updateMessage('minLength', e.target.value)}
                className="input text-sm"
                placeholder={`Must be at least ${field.properties.minLength} characters`}
              />
            </div>
          )}

          {showMaxLength && field.properties.maxLength && (
            <div>
              <label className="label">Max Length Message</label>
              <input
                type="text"
                value={messages.maxLength || ''}
                onChange={(e) => updateMessage('maxLength', e.target.value)}
                className="input text-sm"
                placeholder={`Must be no more than ${field.properties.maxLength} characters`}
              />
            </div>
          )}

          {showMin && field.properties.min !== undefined && (
            <div>
              <label className="label">Minimum Value Message</label>
              <input
                type="text"
                value={messages.min || ''}
                onChange={(e) => updateMessage('min', e.target.value)}
                className="input text-sm"
                placeholder={`Must be at least ${field.properties.min}`}
              />
            </div>
          )}

          {showMax && field.properties.max !== undefined && (
            <div>
              <label className="label">Maximum Value Message</label>
              <input
                type="text"
                value={messages.max || ''}
                onChange={(e) => updateMessage('max', e.target.value)}
                className="input text-sm"
                placeholder={`Must be no more than ${field.properties.max}`}
              />
            </div>
          )}

          {showPattern && (
            <div>
              <label className="label">Pattern Mismatch Message</label>
              <input
                type="text"
                value={messages.pattern || ''}
                onChange={(e) => updateMessage('pattern', e.target.value)}
                className="input text-sm"
                placeholder="Invalid format"
              />
            </div>
          )}

          {showEmail && (
            <div>
              <label className="label">Invalid Email Message</label>
              <input
                type="text"
                value={messages.email || ''}
                onChange={(e) => updateMessage('email', e.target.value)}
                className="input text-sm"
                placeholder="Please enter a valid email address"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Matrix Settings Panel Component
interface MatrixSettingsPanelProps {
  field: FormField;
  updateField: (fieldId: string, updates: Partial<FormField>) => void;
}

function MatrixSettingsPanel({ field, updateField }: MatrixSettingsPanelProps) {
  const rows = field.properties.matrixRows || [];
  const columns = field.properties.matrixColumns || [];

  function addRow() {
    const newRow: MatrixRow = {
      id: `row_${crypto.randomUUID().slice(0, 8)}`,
      label: `Row ${rows.length + 1}`,
    };
    updateField(field.id, {
      properties: { ...field.properties, matrixRows: [...rows, newRow] },
    });
  }

  function updateRow(index: number, label: string) {
    const newRows = [...rows];
    newRows[index] = { ...newRows[index], label };
    updateField(field.id, {
      properties: { ...field.properties, matrixRows: newRows },
    });
  }

  function removeRow(index: number) {
    updateField(field.id, {
      properties: { ...field.properties, matrixRows: rows.filter((_, i) => i !== index) },
    });
  }

  function addColumn() {
    const newColumn: MatrixColumn = {
      id: `col_${crypto.randomUUID().slice(0, 8)}`,
      label: `Option ${columns.length + 1}`,
      value: `option${columns.length + 1}`,
    };
    updateField(field.id, {
      properties: { ...field.properties, matrixColumns: [...columns, newColumn] },
    });
  }

  function updateColumn(index: number, label: string) {
    const newColumns = [...columns];
    newColumns[index] = { ...newColumns[index], label, value: label.toLowerCase().replace(/\s+/g, '_') };
    updateField(field.id, {
      properties: { ...field.properties, matrixColumns: newColumns },
    });
  }

  function removeColumn(index: number) {
    updateField(field.id, {
      properties: { ...field.properties, matrixColumns: columns.filter((_, i) => i !== index) },
    });
  }

  return (
    <div className="p-4 border-b border-gray-200">
      <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Matrix Settings</h3>
      <div className="space-y-4">
        {/* Selection Type */}
        <div>
          <label className="label">Selection Type</label>
          <select
            value={field.properties.matrixType || 'single'}
            onChange={(e) => updateField(field.id, {
              properties: { ...field.properties, matrixType: e.target.value as 'single' | 'multiple' }
            })}
            className="input"
          >
            <option value="single">Single selection per row</option>
            <option value="multiple">Multiple selections per row</option>
          </select>
        </div>

        {/* Rows */}
        <div>
          <label className="label">Rows (Questions)</label>
          <div className="space-y-2 mt-2">
            {rows.map((row, index) => (
              <div key={row.id} className="flex items-center gap-2">
                <input
                  type="text"
                  value={row.label}
                  onChange={(e) => updateRow(index, e.target.value)}
                  className="input flex-1 text-sm"
                  placeholder="Row label"
                />
                <button
                  onClick={() => removeRow(index)}
                  disabled={rows.length <= 1}
                  className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={addRow}
            className="mt-2 text-sm text-primary-600 hover:text-primary-700"
          >
            + Add row
          </button>
        </div>

        {/* Columns */}
        <div>
          <label className="label">Columns (Options)</label>
          <div className="space-y-2 mt-2">
            {columns.map((col, index) => (
              <div key={col.id} className="flex items-center gap-2">
                <input
                  type="text"
                  value={col.label}
                  onChange={(e) => updateColumn(index, e.target.value)}
                  className="input flex-1 text-sm"
                  placeholder="Column label"
                />
                <button
                  onClick={() => removeColumn(index)}
                  disabled={columns.length <= 2}
                  className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={addColumn}
            className="mt-2 text-sm text-primary-600 hover:text-primary-700"
          >
            + Add column
          </button>
        </div>
      </div>
    </div>
  );
}

// Ranking Settings Panel Component
interface RankingSettingsPanelProps {
  field: FormField;
  updateField: (fieldId: string, updates: Partial<FormField>) => void;
}

function RankingSettingsPanel({ field, updateField }: RankingSettingsPanelProps) {
  const items = field.properties.rankingItems || [];

  function addItem() {
    const newItem: RankingItem = {
      id: `item_${crypto.randomUUID().slice(0, 8)}`,
      label: `Item ${items.length + 1}`,
    };
    updateField(field.id, {
      properties: { ...field.properties, rankingItems: [...items, newItem] },
    });
  }

  function updateItem(index: number, label: string) {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], label };
    updateField(field.id, {
      properties: { ...field.properties, rankingItems: newItems },
    });
  }

  function removeItem(index: number) {
    updateField(field.id, {
      properties: { ...field.properties, rankingItems: items.filter((_, i) => i !== index) },
    });
  }

  return (
    <div className="p-4 border-b border-gray-200">
      <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Ranking Items</h3>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={item.id} className="flex items-center gap-2">
            <span className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded text-xs font-medium text-gray-600">
              {index + 1}
            </span>
            <input
              type="text"
              value={item.label}
              onChange={(e) => updateItem(index, e.target.value)}
              className="input flex-1 text-sm"
              placeholder="Item label"
            />
            <button
              onClick={() => removeItem(index)}
              disabled={items.length <= 2}
              className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={addItem}
        className="mt-3 text-sm text-primary-600 hover:text-primary-700"
      >
        + Add item
      </button>
      <p className="text-xs text-gray-400 mt-2">
        Users will drag items to rank them in their preferred order.
      </p>
    </div>
  );
}
